import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useGetSupervisorRequestsQuery,
  useRespondSupervisorRequestMutation,
} from "../api/logbookApi";
import {
  ChevronLeft,
  RefreshCw,
  UserCheck,
  UserX,
  Clock,
  Inbox,
  CheckCircle2,
  XCircle,
  Search,
} from "lucide-react";

function safeMsg(x: any) {
  return String(
    x?.data?.message ||
      x?.message ||
      x?.error ||
      x?.data?.error ||
      "Something went wrong"
  );
}

const TABS = [
  { key: "pending",  label: "Pending",      icon: Clock,        color: "text-amber-600 bg-amber-50" },
  { key: "accepted", label: "Accepted",     icon: CheckCircle2, color: "text-emerald-600 bg-emerald-50" },
  { key: "rejected", label: "Rejected",     icon: XCircle,      color: "text-rose-600 bg-rose-50" },
  { key: "all",      label: "All Requests", icon: Inbox,        color: "text-slate-600 bg-slate-50" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

function getDecision(r: any): "pending" | "accepted" | "rejected" {
  const v = String(r?.status ?? "pending").toLowerCase();
  if (v === "accepted") return "accepted";
  if (v === "rejected") return "rejected";
  return "pending";
}

function fullNameFromMember(m: any) {
  if (!m) return "—";
  const profile = m?.member ?? m;
  const name = [profile?.title, profile?.surname, profile?.firstname, profile?.othernames]
    .filter(Boolean)
    .join(" ");
  return name || m?.name || "—";
}

const StatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    pending:  "bg-amber-100/50 text-amber-700 border-amber-200",
    accepted: "bg-emerald-100/50 text-emerald-700 border-emerald-200",
    rejected: "bg-rose-100/50 text-rose-700 border-rose-200",
  };
  return (
    <span
      className={`text-[10px] font-black px-2.5 py-1 rounded-lg border ${
        styles[status] ?? "bg-slate-100 text-slate-600 border-slate-200"
      }`}
    >
      {status.toUpperCase()}
    </span>
  );
};

export default function SupervisorRequestsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>("pending");
  const [search, setSearch] = useState("");

  const {
    data: allRows = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetSupervisorRequestsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [respond] = useRespondSupervisorRequestMutation();
  const busy = isLoading || isFetching;

  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const counts = useMemo(() => {
    const c = { pending: 0, accepted: 0, rejected: 0, all: allRows.length };
    allRows.forEach((x: any) => { c[getDecision(x)]++; });
    return c;
  }, [allRows]);

  const visible = useMemo(() => {
    let rows: any[] = tab === "all" ? allRows : allRows.filter((x: any) => getDecision(x) === tab);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r: any) => {
        const name = fullNameFromMember(r?.member).toLowerCase();
        const email = (r?.member?.email ?? "").toLowerCase();
        const phone = (r?.member?.member?.phone ?? "").toLowerCase();
        const membership = (r?.member?.member?.membership_no ?? "").toLowerCase();
        return name.includes(q) || email.includes(q) || phone.includes(q) || membership.includes(q);
      });
    }
    return rows;
  }, [allRows, tab, search]);

  const onRespond = async (requestId: string, status: "accepted" | "rejected") => {
    setLocalError(null);
    setLocalSuccess(null);
    setActingId(requestId);
    try {
      const res: any = await respond({ id: requestId, status }).unwrap();
      setLocalSuccess(res?.message || `Request ${status} successfully.`);
      await refetch();
    } catch (e: any) {
      setLocalError(safeMsg(e));
    } finally {
      setActingId(null);
    }
  };

  // Accept / Reject actions — shared by the desktop table and the mobile cards.
  // `block` makes the buttons stretch full-width (used on mobile).
  const renderActions = (r: any, block = false) => {
    const rowBusy = actingId === r.id;
    if (getDecision(r) !== "pending") {
      return <span className="text-xs font-black text-slate-300 uppercase tracking-widest">—</span>;
    }
    return (
      <div className={`flex items-center gap-2 ${block ? "w-full" : "justify-end"}`}>
        <button
          onClick={() => onRespond(r.id, "rejected")}
          disabled={busy || rowBusy}
          className={`${block ? "flex-1 justify-center" : ""} px-4 py-2 rounded-xl border-2 border-slate-100 text-xs font-black text-rose-600 hover:bg-rose-50 hover:border-rose-100 transition-all flex items-center gap-1.5 disabled:opacity-50`}
        >
          <UserX size={14} /> {rowBusy ? "…" : "Reject"}
        </button>
        <button
          onClick={() => onRespond(r.id, "accepted")}
          disabled={busy || rowBusy}
          className={`${block ? "flex-1 justify-center" : ""} px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-black hover:bg-primary transition-all flex items-center gap-1.5 disabled:opacity-50`}
        >
          <UserCheck size={14} /> {rowBusy ? "…" : "Accept"}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 sm:pb-32">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10 py-6 sm:py-12">
        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-5 sm:gap-8 mb-8 sm:mb-12">
          <div>
            <nav className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 sm:mb-4">
              <Link to="/dashboard" className="hover:text-primary transition-colors">
                Dashboard
              </Link>
              <span>/</span>
              <span className="text-slate-900">Supervision Requests</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-[1000] text-slate-900 tracking-tight leading-none">
              Requests<span className="text-primary italic">.</span>
            </h1>
            <p className="text-slate-500 text-base sm:text-lg mt-3 sm:mt-4 font-medium max-w-xl">
              Manage incoming supervision requests from probationers assigned to you.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 md:flex-none justify-center px-5 sm:px-6 py-3 sm:py-3.5 rounded-2xl text-sm font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-all flex items-center gap-2"
            >
              <ChevronLeft size={18} /> Back
            </button>
            <button
              onClick={() => refetch()}
              disabled={busy}
              className="flex-1 md:flex-none justify-center px-5 sm:px-8 py-3 sm:py-3.5 bg-slate-900 text-white rounded-2xl text-sm font-bold hover:bg-primary transition-all shadow-xl shadow-slate-200 flex items-center gap-2"
            >
              <RefreshCw size={18} className={busy ? "animate-spin" : ""} />
              {busy ? "Syncing..." : "Refresh"}
            </button>
          </div>
        </header>

        {/* ALERTS */}
        {(localSuccess || localError || error) && (
          <div className="mb-8 space-y-3">
            {localSuccess && (
              <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-800 font-bold flex items-center gap-3">
                <CheckCircle2 size={20} /> {localSuccess}
              </div>
            )}
            {(localError || error) && (
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 font-bold flex items-center gap-3">
                <XCircle size={20} /> {localError || safeMsg(error)}
              </div>
            )}
          </div>
        )}

        {/* STAT CARDS / TAB FILTERS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
          {TABS.map((t) => {
            const isActive = tab === t.key;
            const Icon = t.icon;
            const countValue = t.key === "all" ? counts.all : counts[t.key as keyof typeof counts];
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setSearch(""); setLocalSuccess(null); }}
                className={`flex flex-col items-start p-4 sm:p-6 rounded-2xl sm:rounded-[2rem] border-2 transition-all duration-300 text-left ${
                  isActive
                    ? "bg-white border-slate-900 shadow-xl shadow-slate-100 md:-translate-y-1"
                    : "bg-slate-50 border-transparent hover:bg-slate-100"
                }`}
              >
                <div className={`p-2 rounded-xl mb-3 sm:mb-4 ${t.color}`}>
                  <Icon size={20} />
                </div>
                <p className="text-[10px] sm:text-[11px] font-black uppercase tracking-widest text-slate-400">
                  {t.label}
                </p>
                <p className={`text-xl sm:text-2xl font-[1000] ${isActive ? "text-slate-900" : "text-slate-600"}`}>
                  {countValue}
                </p>
              </button>
            );
          })}
        </div>

        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or membership no…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-2xl border-2 border-slate-100 bg-white text-sm font-medium text-slate-700 placeholder-slate-300 focus:outline-none focus:border-slate-900 transition-colors"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden">
          {busy && allRows.length === 0 ? (
            <div className="py-20 flex flex-col items-center gap-4 animate-pulse">
              <div className="w-12 h-12 bg-slate-200 rounded-full" />
              <div className="h-4 w-48 bg-slate-200 rounded-full" />
            </div>
          ) : visible.length === 0 ? (
            <div className="text-center py-32">
              <Inbox size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-black text-slate-900">
                No {tab} requests
              </h3>
              <p className="text-slate-400 font-medium">
                {search ? "Try adjusting your search." : "Everything is up to date."}
              </p>
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="mt-4 text-sm font-bold text-primary hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop / tablet: table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60">
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4 w-12">#</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Applicant</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4 hidden md:table-cell">Email</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4 hidden lg:table-cell">Last Updated</th>
                      <th className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest px-4 py-4">Status</th>
                      <th className="text-right text-[10px] font-black text-slate-400 uppercase tracking-widest px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {visible.map((r: any, idx: number) => {
                      const decision = getDecision(r);
                      const name = fullNameFromMember(r?.member);
                      const phone = r?.member?.member?.phone;
                      const membershipNo = r?.member?.member?.membership_no;
                      const email = r?.member?.email;

                      return (
                        <tr key={r.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-6 py-5 text-sm font-black text-slate-300">{idx + 1}</td>

                          <td className="px-4 py-5">
                            <div className="font-[900] text-slate-900 text-sm leading-tight">{name}</div>
                            {phone && (
                              <div className="text-xs text-slate-400 font-medium mt-0.5">{phone}</div>
                            )}
                            {membershipNo && (
                              <div className="font-mono text-[10px] text-indigo-500 font-black mt-1">
                                #{membershipNo}
                              </div>
                            )}
                          </td>

                          <td className="px-4 py-5 hidden md:table-cell">
                            <span className="text-sm text-slate-500 font-medium">{email ?? "—"}</span>
                          </td>

                          <td className="px-4 py-5 hidden lg:table-cell">
                            <span className="text-sm text-slate-500 font-medium">
                              {r?.updated_at
                                ? new Date(r.updated_at).toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })
                                : "N/A"}
                            </span>
                          </td>

                          <td className="px-4 py-5">
                            <StatusBadge status={decision} />
                          </td>

                          <td className="px-6 py-5">{renderActions(r)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile: cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {visible.map((r: any) => {
                  const decision = getDecision(r);
                  const name = fullNameFromMember(r?.member);
                  const phone = r?.member?.member?.phone;
                  const membershipNo = r?.member?.member?.membership_no;
                  const email = r?.member?.email;

                  return (
                    <div key={r.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-[900] text-slate-900 text-sm leading-tight break-words">{name}</div>
                          {email && (
                            <div className="text-xs text-slate-500 font-medium mt-0.5 break-all">{email}</div>
                          )}
                          {phone && (
                            <div className="text-xs text-slate-400 font-medium mt-0.5">{phone}</div>
                          )}
                          {membershipNo && (
                            <div className="font-mono text-[10px] text-indigo-500 font-black mt-1">#{membershipNo}</div>
                          )}
                        </div>
                        <StatusBadge status={decision} />
                      </div>

                      <div className="text-[11px] text-slate-400 font-bold uppercase tracking-wide">
                        {r?.updated_at
                          ? new Date(r.updated_at).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : "N/A"}
                      </div>

                      {decision === "pending" && renderActions(r, true)}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {visible.length > 0 && (
            <div className="px-6 py-4 border-t border-slate-50 flex items-center justify-between">
              <p className="text-xs font-bold text-slate-400">
                Showing {visible.length} of {allRows.length} request{allRows.length !== 1 ? "s" : ""}
              </p>
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="text-xs font-bold text-primary hover:underline"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
